import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Check, X, MessageSquare, ArrowLeft } from 'lucide-react';
import MediaEntryList from '../submission/MediaEntryList';

interface AnimatronicReviewProps {
  submissionId: string;
  onReviewComplete?: () => void;
}

const AnimatronicReview: React.FC<AnimatronicReviewProps> = ({ 
  submissionId, 
  onReviewComplete
}) => {
  const navigate = useNavigate();
  const [submission, setSubmission] = useState<any>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSubmission = async () => {
      try {
        setIsLoading(true);
        
        const { data: animatronicSubmission, error: submissionError } = await supabase
          .from('animatronic_submissions')
          .select(`
            *,
            user:user_profiles(
              display_name,
              email
            )
          `)
          .eq('id', submissionId)
          .single();

        if (submissionError) throw submissionError;

        setSubmission(animatronicSubmission);
      } catch (err) {
        console.error('Error fetching submission:', err);
        setError(err instanceof Error ? err.message : 'Failed to load submission');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubmission();
  }, [submissionId]);

  const handleApprove = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Just update the submission status
      const { error } = await supabase
        .from('animatronic_submissions')
        .update({
          approved: true,
          admin_view: false
        })
        .eq('id', submissionId);

      if (error) throw error;

      navigate('/admin');
    } catch (err) {
      console.error('Error approving submission:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve submission');
      setIsLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { error } = await supabase.rpc('reject_submission', {
        target_submission_id: submissionId,
        notes: adminNotes
      });
      if (error) throw error;

      navigate('/admin');
    } catch (err) {
      console.error('Error rejecting submission:', err);
      setError(err instanceof Error ? err.message : 'Failed to reject submission');
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading submission...</div>;
  }

  if (error || !submission) {
    return (
      <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
        {error || 'Failed to load submission'}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-content-primary">
            {submission.name}
          </h2>
          <p className="text-content-secondary">
            Submitted by {submission.user.display_name} ({submission.user.email})
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center px-4 py-2 bg-background border border-gray-800 text-content-secondary hover:text-content-primary rounded-lg"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          <button
            onClick={handleReject}
            disabled={isLoading}
            className="flex items-center px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20"
          >
            <X className="w-4 h-4 mr-2" />
            Reject
          </button>
          <button
            onClick={handleApprove}
            disabled={isLoading}
            className="flex items-center px-4 py-2 bg-brand hover:bg-brand-dark text-white rounded-lg"
          >
            <Check className="w-4 h-4 mr-2" />
            Approve
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-content-secondary">Year</label>
                <span className="text-content-primary">{submission.year}</span>
              </div>
              <div>
                <label className="block text-sm text-content-secondary">Status</label>
                <span className="text-content-primary capitalize">{submission.status}</span>
              </div>
              <div>
                <label className="block text-sm text-content-secondary">Type</label>
                <span className="text-content-primary capitalize">{submission.type}</span>
              </div>
              <div>
                <label className="block text-sm text-content-secondary">Brand</label>
                <span className="text-content-primary">{submission.brand}</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Location</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-content-secondary">Country</label>
                <span className="text-content-primary">{submission.country}</span>
              </div>
              <div>
                <label className="block text-sm text-content-secondary">Park</label>
                <span className="text-content-primary">{submission.park}</span>
              </div>
              <div>
                <label className="block text-sm text-content-secondary">Attraction</label>
                <span className="text-content-primary">{submission.attraction}</span>
              </div>
              <div>
                <label className="block text-sm text-content-secondary">GPS</label>
                <span className="text-content-primary">{submission.gps_coordinates}</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Description</h3>
            <div className="prose prose-invert max-w-none bg-background p-4 rounded-lg border border-gray-800">
              <div dangerouslySetInnerHTML={{ __html: submission.description }} />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Media</h3>
            <MediaEntryList
              entries={submission.media}
              readOnly
            />
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Admin Notes</h3>
            <div className="flex items-start gap-2">
              <MessageSquare className="w-5 h-5 mt-2 text-content-secondary" />
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes for the submitter (optional)..."
                className="flex-1 min-h-[100px] p-4 bg-background border border-gray-800 rounded-lg resize-none"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimatronicReview;
